import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAccountStore } from '@/stores/account'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean
    requiresAccount?: boolean
    requiresManager?: boolean
    requiresSuperuser?: boolean
    title?: string
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/sign-in', component: () => import('@/features/auth/SignInView.vue'), meta: { public: true, title: 'Sign in' } },
  { path: '/auth/callback', component: () => import('@/features/auth/AuthCallbackView.vue'), meta: { public: true, title: 'Signing in' } },
  { path: '/onboarding', component: () => import('@/features/auth/OnboardingView.vue'), meta: { title: 'Welcome' } },
  { path: '/select-account', component: () => import('@/features/accounts/AccountSelectView.vue'), meta: { title: 'Select account' } },
  { path: '/privacy', component: () => import('@/features/legal/PrivacyView.vue'), meta: { public: true, title: 'Privacy' } },
  { path: '/terms', component: () => import('@/features/legal/TermsView.vue'), meta: { public: true, title: 'Terms' } },
  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    meta: { requiresAccount: true },
    children: [
      { path: '', name: 'dashboard', component: () => import('@/features/dashboard/DashboardView.vue'), meta: { title: 'Overview' } },
      { path: 'companies', name: 'companies', component: () => import('@/features/companies/CompaniesView.vue'), meta: { title: 'Companies' } },
      { path: 'companies/:id', name: 'company', component: () => import('@/features/companies/CompanyDetailView.vue'), meta: { title: 'Company intelligence' } },
      { path: 'profiles', component: () => import('@/features/profiles/ProfilesView.vue'), meta: { title: 'Saved profiles' } },
      { path: 'definitions', component: () => import('@/features/definitions/DefinitionsView.vue'), meta: { requiresManager: true, title: 'Definitions' } },
      { path: 'scoring', component: () => import('@/features/scoring/ScoringView.vue'), meta: { requiresManager: true, title: 'Scoring' } },
      { path: 'monitoring', component: () => import('@/features/monitoring/MonitoringView.vue'), meta: { requiresManager: true, title: 'Monitoring' } },
      { path: 'members', component: () => import('@/features/members/MembersView.vue'), meta: { requiresManager: true, title: 'Members' } },
      { path: 'jobs', component: () => import('@/features/jobs/JobsView.vue'), meta: { title: 'My jobs' } },
    ],
  },
  {
    path: '/admin',
    component: () => import('@/layouts/AppShell.vue'),
    meta: { requiresSuperuser: true },
    children: [
      { path: '', redirect: '/admin/accounts' },
      { path: 'accounts', component: () => import('@/features/admin/AdminAccountsView.vue'), meta: { title: 'Accounts' } },
      { path: 'ingest', component: () => import('@/features/admin/AdminIngestView.vue'), meta: { title: 'Source ingest' } },
      { path: 'failures', component: () => import('@/features/admin/AdminFailuresView.vue'), meta: { title: 'Failures' } },
      { path: 'jobs/:id', component: () => import('@/features/admin/AdminJobDetailView.vue'), meta: { title: 'Research job' } },
      { path: 'ai-settings', component: () => import('@/features/admin/AdminAiSettingsView.vue'), meta: { title: 'AI settings' } },
      { path: 'prompts', component: () => import('@/features/admin/AdminPromptsView.vue'), meta: { title: 'Framework prompts' } },
      { path: 'catalogue', component: () => import('@/features/admin/AdminCatalogueView.vue'), meta: { title: 'Catalogue promotion' } },
    ],
  },
  { path: '/forbidden', component: () => import('@/features/legal/ForbiddenView.vue'), meta: { public: true, title: 'Access denied' } },
  { path: '/:pathMatch(.*)*', component: () => import('@/features/legal/NotFoundView.vue'), meta: { public: true, title: 'Page not found' } },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  await auth.initialise()
  document.title = `${to.meta.title ?? 'Workspace'} · Marketal`

  if (to.meta.public) {
    if (to.path === '/sign-in' && auth.authenticated) return auth.onboarded ? '/' : '/onboarding'
    return true
  }
  if (!auth.authenticated) return { path: '/sign-in', query: { next: to.fullPath } }
  if (!auth.onboarded && to.path !== '/onboarding') return '/onboarding'
  if (auth.onboarded && to.path === '/onboarding') return '/'

  const account = useAccountStore()
  if (to.meta.requiresSuperuser && !auth.viewer?.is_superuser) return '/forbidden'
  if (to.meta.requiresAccount && !account.selectedId) {
    return { path: '/select-account', query: { next: to.fullPath } }
  }
  if (to.meta.requiresManager && !account.canManage) return '/forbidden'
  return true
})
